import { useQuery } from '@tanstack/react-query';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export type ApiRequest_Get_Posts = {
  communityId?: number;
  groupId?: number;
  page?: number;
  limit?: number;
};

export type PostResponse = {
  id: number;
  title: string;
  content: string;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
  community_id?: number;
  group_negotiation_id?: number;
  author: {
    id: number;
    user: {
      id: string;
      full_name: string;
      email: string;
    };
  };
  _count: {
    replies: number;
    likes: number;
  };
  is_liked?: boolean;
  documentTags?: Array<{
    id: number;
    document: {
      id: string;
      original_name: string;
      mime_type: string;
      size: number;
    };
  }>;
};

export type ApiResponse_Get_Posts = PostResponse[];

const COMMUNITY_METHOD = 'GET';
const COMMUNITY_ENDPOINT = 'posts/community/{communityId}';

const GROUP_METHOD = 'GET';
const GROUP_ENDPOINT = 'posts/group/{groupId}';

export const COMMUNITY_POSTS_QUERY_KEY = (params: {
  communityId: number;
  page?: number;
  limit?: number;
}) =>
  getQueryKey(
    COMMUNITY_METHOD,
    getResolvedEndpoint(COMMUNITY_ENDPOINT, { communityId: params.communityId.toString() }),
    { page: params.page || 1, limit: params.limit || 20 }
  );

export const GROUP_POSTS_QUERY_KEY = (params: { groupId: number; page?: number; limit?: number }) =>
  getQueryKey(
    GROUP_METHOD,
    getResolvedEndpoint(GROUP_ENDPOINT, { groupId: params.groupId.toString() }),
    { page: params.page || 1, limit: params.limit || 20 }
  );

export const useGetCommunityPosts = (
  params: { communityId: number; page?: number; limit?: number },
  enabled = true
) => {
  const { session } = useAuth();

  return useQuery({
    enabled: enabled && !!session?.access_token && !!params.communityId,
    queryKey: COMMUNITY_POSTS_QUERY_KEY(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());

      const endpoint = getResolvedEndpoint(COMMUNITY_ENDPOINT, {
        communityId: params.communityId.toString(),
      });
      const url = queryParams.toString() ? `${endpoint}?${queryParams.toString()}` : endpoint;

      const data = await handleApiCall<ApiResponse_Get_Posts, Record<string, never>>(
        session,
        COMMUNITY_METHOD,
        url,
        {}
      );

      return data;
    },
  });
};

export const useGetGroupPosts = (
  params: { groupId: number; page?: number; limit?: number },
  enabled = true
) => {
  const { session } = useAuth();

  return useQuery({
    enabled: enabled && !!session?.access_token && !!params.groupId,
    queryKey: GROUP_POSTS_QUERY_KEY(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());

      const endpoint = getResolvedEndpoint(GROUP_ENDPOINT, {
        groupId: params.groupId.toString(),
      });
      const url = queryParams.toString() ? `${endpoint}?${queryParams.toString()}` : endpoint;

      const data = await handleApiCall<ApiResponse_Get_Posts, Record<string, never>>(
        session,
        GROUP_METHOD,
        url,
        {}
      );

      return data;
    },
  });
};
